import { useState, useEffect } from 'react';
import { useLocation } from 'wouter';
import { useGame } from '../state/GameContext.jsx';
import { useNoScroll } from '../hooks/useNoScroll.js';
import RoundResultView from '../components/RoundResultView.jsx';
import FinalView from '../components/FinalView.jsx';

export default function HostFlowScreen() {
  const [, setLocation] = useLocation();
  const { state, dispatch, connect, send, ROOM_WORKER_URL } = useGame();
  const { room, wsStatus, currentReveal } = state;
  useNoScroll(room?.status === 'playing');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // 部屋を作成して接続
  useEffect(() => {
    if (state.roomCode) return; // 既に作成済み
    setLoading(true);
    fetch(`${ROOM_WORKER_URL}/api/room/create`, { method: 'POST' })
      .then((r) => r.json())
      .then(({ roomCode, hostId, hostToken }) => {
        dispatch({ type: 'SET_SESSION', payload: { role: 'host', roomCode, participantId: hostId, token: hostToken } });
        connect(roomCode, hostId, hostToken);
        setLoading(false);
      })
      .catch(() => {
        setError('ルームの作成に失敗しました');
        setLoading(false);
      });
  }, []); // eslint-disable-line

  if (loading || wsStatus === 'connecting') {
    return <LoadingScreen label="ルームを作成中…" />;
  }
  if (error) {
    return (
      <div className="min-h-screen tv-bg-stripe flex flex-col items-center justify-center gap-4 p-6">
        <p className="text-tv-red font-body">{error}</p>
        <button className="tv-btn tv-btn-white" onClick={() => setLocation('/')}>トップへ</button>
      </div>
    );
  }
  if (!room) return <LoadingScreen label="接続中…" />;

  if (room.status === 'lobby') return <HostLobby room={room} send={send} setLocation={setLocation} roomCode={state.roomCode} />;
  if (room.status === 'playing') return <HostPlaying room={room} send={send} />;
  if (room.status === 'scoring') return <HostScoring room={room} send={send} currentReveal={currentReveal} />;
  if (room.status === 'roundResult') return <RoundResultView isHost />;
  if (room.status === 'final') return <FinalView room={room} />;

  return <LoadingScreen label="待機中…" />;
}

// ── Lobby ──────────────────────────────────────────────
function HostLobby({ room, send, setLocation, roomCode }) {
  const joinUrl = `${location.origin}/join`;

  return (
    <div className="min-h-screen tv-bg-stripe flex flex-col items-center p-4 gap-4 overflow-y-auto">
      <button className="self-start tv-btn tv-btn-white text-sm px-3 py-1 mt-2" onClick={() => setLocation('/')}>
        ← 終了
      </button>

      <div className="tv-panel w-full max-w-sm p-5 flex flex-col items-center gap-3">
        <p className="font-body text-sm text-tv-ash">参加用ルームコード</p>
        <p className="font-num text-5xl font-bold tracking-widest text-tv-red">{roomCode}</p>
        <p className="font-body text-xs text-tv-ash break-all text-center">{joinUrl}</p>
      </div>

      <div className="tv-panel-sm w-full max-w-sm p-3 flex flex-col gap-2">
        <div className="flex items-center justify-between">
          <p className="font-body text-sm font-bold">ラウンド数</p>
          <div className="flex items-center gap-2">
            {[1, 2, 3, 4, 5].map((n) => (
              <button
                key={n}
                onClick={() => send({ type: 'host:set-rounds', rounds: n })}
                className={`w-9 h-9 font-num font-bold rounded-lg border-4 ${
                  room.totalRounds === n
                    ? 'bg-tv-yellow border-tv-sumi'
                    : 'bg-tv-ivory border-tv-ash text-tv-ash'
                }`}
              >
                {n}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="tv-panel w-full max-w-sm p-4">
        <div className="flex items-center justify-between mb-2">
          <p className="font-body text-sm font-bold">
            参加者 ({room.participants.filter((p) => p.connected).length}人)
          </p>
          <button
            className="tv-btn tv-btn-white text-xs px-2 py-1 disabled:opacity-40"
            disabled={room.participants.length >= 8}
            onClick={() => send({ type: 'host:add-npc' })}
          >
            🤖 NPC追加
          </button>
        </div>
        {room.participants.length === 0 ? (
          <p className="text-tv-ash font-body text-sm text-center py-2">まだ誰もいません…</p>
        ) : (
          <div className="flex flex-col gap-2">
            {room.participants.map((p) => (
              <div key={p.id} className="flex items-center gap-2">
                <span className="text-xl">{p.emoji}</span>
                <span className="font-body flex-1">
                  {p.nickname}
                  {p.isNpc && <span className="ml-2 text-xs text-tv-ash">(NPC)</span>}
                </span>
                {p.isNpc ? (
                  <button
                    className="text-xs text-tv-red font-bold px-2"
                    onClick={() => send({ type: 'host:remove-npc', participantId: p.id })}
                  >
                    ×
                  </button>
                ) : (
                  <span className={`text-xs font-body ${p.connected ? 'text-tv-green' : 'text-tv-ash'}`}>
                    {p.connected ? '接続中' : '切断'}
                  </span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <button
        className="tv-btn tv-btn-red text-xl py-4 px-10"
        disabled={room.participants.filter((p) => p.connected).length === 0}
        onClick={() => send({ type: 'host:start-round' })}
      >
        ゲーム開始！
      </button>
    </div>
  );
}

// ── Playing（回答待ち）──────────────────────────────────
function HostPlaying({ room, send }) {
  const [elapsed, setElapsed] = useState(0);
  const TIME_LIMIT = 30;
  const answeredIds = Object.keys(room.currentAnswers ?? {});
  const connected = room.participants.filter((p) => p.connected);

  useEffect(() => {
    const timer = setInterval(() => setElapsed((e) => e + 1), 1000);
    return () => clearInterval(timer);
  }, []);

  const remaining = Math.max(0, TIME_LIMIT - elapsed);
  const isUrgent = remaining <= 10;

  return (
    <div className="min-h-screen tv-bg-stripe flex flex-col items-center p-4 gap-5">
      <div className="tv-panel w-full max-w-sm p-4 mt-4 flex flex-col items-center gap-2">
        <p className="font-body text-sm text-tv-ash">お題</p>
        <p className="tv-heading text-2xl text-center">{room.currentPrompt?.text}</p>
        <span className="tv-telop text-xs">
          {room.currentPrompt?.type === 'draw' ? '🖊 お絵かき' : '📝 テキスト'}
        </span>
      </div>

      <div className={`font-num text-6xl font-bold ${isUrgent ? 'text-tv-red animate-pulse-danger' : 'text-tv-blue'}`}>
        {remaining}
      </div>

      <div className="tv-panel w-full max-w-sm p-4">
        <p className="font-body text-sm font-bold mb-2">
          回答状況 ({answeredIds.length}/{connected.length})
        </p>
        <div className="grid grid-cols-3 gap-2">
          {room.participants.map((p) => (
            <div key={p.id} className={`tv-panel-sm p-2 flex flex-col items-center gap-1 ${answeredIds.includes(p.id) ? 'bg-green-50' : ''}`}>
              <span className="text-xl">{p.emoji}</span>
              <span className="font-body text-xs truncate w-full text-center">{p.nickname}</span>
              <span className="text-sm">{answeredIds.includes(p.id) ? '✅' : '…'}</span>
            </div>
          ))}
        </div>
      </div>

      <button
        className="tv-btn tv-btn-sumi text-sm px-5 py-2"
        onClick={() => send({ type: 'host:begin-scoring' })}
      >
        時間切れ→採点へ
      </button>
    </div>
  );
}

// ── Scoring（ホスト採点）────────────────────────────────
function HostScoring({ room, send, currentReveal }) {
  const [savedId, setSavedId] = useState(null);
  const scored = Object.keys(room.currentScores ?? {});
  const totalToScore = Object.keys(room.currentAnswers ?? {}).length;

  const saveToGallery = () => {
    const entry = {
      id: Date.now(),
      name: currentReveal.nickname,
      emoji: currentReveal.emoji,
      prompt: room.currentPrompt?.text ?? '',
      dataUrl: currentReveal.dataUrl,
      savedAt: new Date().toISOString(),
    };
    const existing = JSON.parse(localStorage.getItem('saiten-man-gallery') ?? '[]');
    existing.unshift(entry);
    localStorage.setItem('saiten-man-gallery', JSON.stringify(existing.slice(0, 200)));
    setSavedId(currentReveal.participantId);
  };

  return (
    <div className="min-h-screen tv-bg-stripe flex flex-col items-center p-4 gap-4">
      <div className="tv-telop text-lg mt-4">
        採点中 ({scored.length}/{totalToScore})
      </div>

      {currentReveal ? (
        <div className="tv-panel w-full max-w-sm p-4 flex flex-col items-center gap-3">
          <div className="flex items-center gap-2">
            <span className="text-3xl">{currentReveal.emoji}</span>
            <span className="font-body font-bold text-lg">{currentReveal.nickname}</span>
          </div>

          {currentReveal.answerType === 'text' ? (
            <div className="w-full bg-tv-ivory border-2 border-tv-sumi rounded-tv p-4 min-h-[80px] font-body text-lg text-center">
              {currentReveal.text || '（未回答）'}
            </div>
          ) : currentReveal.dataUrl ? (
            <>
              <img
                src={currentReveal.dataUrl}
                alt="回答"
                className="w-full border-4 border-tv-sumi rounded-tv"
              />
              <button
                onClick={saveToGallery}
                className="tv-btn tv-btn-white text-sm px-4 py-1 self-end"
              >
                {savedId === currentReveal.participantId ? '✅ 保存済み' : '💾 ギャラリーに保存'}
              </button>
            </>
          ) : (
            <div className="w-full bg-tv-ivory border-2 border-tv-sumi rounded-tv p-4 text-center text-tv-ash font-body">
              （未回答）
            </div>
          )}

          <p className="font-body text-sm text-tv-ash">何点？</p>
          <div className="flex gap-2">
            {[1, 2, 3, 4, 5].map((stars) => (
              <button
                key={stars}
                className="tv-btn tv-btn-yellow text-2xl w-14 h-14"
                onClick={() => send({ type: 'host:score', participantId: currentReveal.participantId, stars })}
              >
                {'★'.repeat(stars).length <= 3 ? '★'.repeat(stars) : stars}
              </button>
            ))}
          </div>
          <div className="flex gap-1 text-sm text-tv-ash font-body">
            {['★1', '★★2', '★★★3', '★4', '★★★★★5'].map((_, i) => (
              <span key={i} className="w-14 text-center">{i + 1}pt</span>
            ))}
          </div>
        </div>
      ) : (
        <div className="tv-panel w-full max-w-sm p-6 flex items-center justify-center">
          <p className="text-tv-ash font-body">回答を読み込み中…</p>
        </div>
      )}

      <div className="w-full max-w-sm flex flex-col gap-2">
        {room.participants.map((p) => {
          const sc = (room.currentScores ?? {})[p.id];
          return (
            <div key={p.id} className="tv-panel-sm p-2 flex items-center gap-2">
              <span>{p.emoji}</span>
              <span className="flex-1 font-body text-sm truncate">{p.nickname}</span>
              {sc ? (
                <span className="font-num text-tv-green font-bold">★{sc.stars}</span>
              ) : currentReveal?.participantId === p.id ? (
                <span className="font-body text-xs text-tv-yellow">採点中</span>
              ) : (
                <span className="font-body text-xs text-tv-ash">待機</span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function LoadingScreen({ label }) {
  return (
    <div className="min-h-screen tv-bg-stripe flex items-center justify-center">
      <p className="font-body text-tv-ash animate-pulse">{label}</p>
    </div>
  );
}
